const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const CIF_CONTROL_LETTERS = "JABCDEFGHI";
const CIF_LETTER_CONTROL = new Set(["P", "Q", "R", "S", "N", "W"]);
const CIF_DIGIT_CONTROL = new Set(["A", "B", "E", "H"]);

const sanitize = (value: string): string => (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const calculateNifLetter = (num: number): string => DNI_LETTERS[num % 23];

const isValidDni = (value: string): boolean => {
  if (!/^\d{8}[A-Z]$/.test(value)) return false;
  const num = parseInt(value.slice(0, 8), 10);
  const letter = value.slice(8);
  return calculateNifLetter(num) === letter;
};

const isValidNie = (value: string): boolean => {
  if (!/^[XYZ]\d{7}[A-Z]$/.test(value)) return false;
  const map: Record<string, string> = { X: "0", Y: "1", Z: "2" };
  const numeric = `${map[value[0]]}${value.slice(1, 8)}`;
  const letter = value.slice(8);
  return calculateNifLetter(parseInt(numeric, 10)) === letter;
};

const isValidCif = (value: string): boolean => {
  if (!/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(value)) return false;

  const letter = value[0];
  const body = value.slice(1, 8);
  const control = value[8];

  let evenSum = 0;
  let oddSum = 0;

  for (let i = 0; i < body.length; i++) {
    const digit = parseInt(body[i], 10);
    if (i % 2 === 0) {
      const doubled = digit * 2;
      oddSum += Math.floor(doubled / 10) + (doubled % 10);
    } else {
      evenSum += digit;
    }
  }

  const total = evenSum + oddSum;
  const unit = (10 - (total % 10)) % 10;
  const expectedDigit = String(unit);
  const expectedLetter = CIF_CONTROL_LETTERS[unit];

  if (CIF_DIGIT_CONTROL.has(letter)) return control === expectedDigit;
  if (CIF_LETTER_CONTROL.has(letter)) return control === expectedLetter;
  return control === expectedDigit || control === expectedLetter;
};

export const normalizeSpanishTaxId = (value: string): string => sanitize(value);

export const validateSpanishTaxId = (
  value: string
): { valid: boolean; normalized: string; type?: "NIF" | "NIE" | "CIF"; message?: string } => {
  const normalized = normalizeSpanishTaxId(value);
  if (!normalized) return { valid: false, normalized, message: "El NIF/NIE/CIF es obligatorio" };

  if (isValidDni(normalized)) return { valid: true, normalized, type: "NIF" };
  if (isValidNie(normalized)) return { valid: true, normalized, type: "NIE" };
  if (isValidCif(normalized)) return { valid: true, normalized, type: "CIF" };

  if (normalized.length !== 9) {
    return { valid: false, normalized, message: "Debe tener 9 caracteres" };
  }
  return { valid: false, normalized, message: "NIF/NIE/CIF no valido" };
};

export const normalizeIban = (value: string): string => sanitize(value);

export const formatIban = (value: string): string => {
  const iban = normalizeIban(value);
  return iban.replace(/(.{4})/g, "$1 ").trim();
};

const ibanToNumeric = (iban: string): string => {
  return iban
    .slice(4)
    .concat(iban.slice(0, 4))
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0);
      if (code >= 65 && code <= 90) return String(code - 55);
      return char;
    })
    .join("");
};

const mod97 = (numericIban: string): number => {
  let remainder = 0;
  for (let i = 0; i < numericIban.length; i += 7) {
    remainder = parseInt(String(remainder) + numericIban.substring(i, i + 7), 10) % 97;
  }
  return remainder;
};

export const validateSpanishIban = (
  value: string
): { valid: boolean; normalized: string; message?: string } => {
  const normalized = normalizeIban(value);
  if (!normalized) return { valid: false, normalized, message: "El IBAN es obligatorio" };
  if (!/^ES\d{22}$/.test(normalized)) {
    return { valid: false, normalized, message: "El IBAN espanol debe tener formato ES + 22 digitos" };
  }

  const numeric = ibanToNumeric(normalized);
  if (mod97(numeric) !== 1) {
    return { valid: false, normalized, message: "IBAN no valido" };
  }
  return { valid: true, normalized };
};
